import { useMemo } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getDailyInput } from '../services'
import { CustomerPipelineFilters } from '../types'

dayjs.extend(isoWeek)

export type Granularity = 'day' | 'week' | 'month'

/** Determine chart granularity based on date range span */
export const getGranularity = (gte?: string, lte?: string): Granularity => {
    if (!gte || !lte) return 'day'
    const diffDays = dayjs(lte).diff(dayjs(gte), 'day')
    if (diffDays <= 90) return 'day'
    if (diffDays <= 180) return 'week'
    return 'month'
}

/** Get period key from a date based on granularity */
const getPeriodKey = (date: dayjs.Dayjs, granularity: Granularity): string => {
    switch (granularity) {
        case 'week':
            return `${date.isoWeekYear()}-W${String(date.isoWeek()).padStart(2, '0')}`
        case 'month':
            return date.format('YYYY-MM')
        default:
            return date.format('YYYY-MM-DD')
    }
}

/** Generate all period slots in a date range, filled with zeros */
const generatePeriodSlots = (
    gte: string,
    lte: string,
    granularity: Granularity
) => {
    const start = dayjs(gte)
    const end = dayjs(lte)
    const slots: Record<
        string,
        { key: string; date: string; new_customers_count: number }
    > = {}

    let current = start
    while (current.isBefore(end) || current.isSame(end, 'day')) {
        const key = getPeriodKey(current, granularity)
        if (!slots[key]) {
            const periodDate =
                granularity === 'week'
                    ? current.startOf('isoWeek').format('YYYY-MM-DD')
                    : granularity === 'month'
                      ? current.startOf('month').format('YYYY-MM-DD')
                      : current.format('YYYY-MM-DD')
            slots[key] = { key, date: periodDate, new_customers_count: 0 }
        }
        // Advance cursor
        current =
            granularity === 'week'
                ? current.add(1, 'week').startOf('isoWeek')
                : granularity === 'month'
                  ? current.add(1, 'month').startOf('month')
                  : current.add(1, 'day')
    }
    return slots
}

export interface DailyInputItem {
    date: string
    new_customers_count: number
    sales_manager_code: number
    employee_name: string
}

export const useDailyInputData = (filters: CustomerPipelineFilters) => {
    // Previous period filters (same span, shifted back)
    const previousFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null
        const start = dayjs(filters.gte)
        const diffDays = dayjs(filters.lte).diff(start, 'day') + 1
        return {
            ...filters,
            gte: start.subtract(diffDays, 'day').format('YYYY-MM-DD'),
            lte: start.subtract(1, 'day').format('YYYY-MM-DD'),
        }
    }, [filters])

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
            'daily-input',
            filters.gte,
            filters.lte,
            filters.sales_manager_codes,
        ],
        queryFn: () => getDailyInput(filters),
        enabled: !!filters.gte && !!filters.lte,
    })

    const { data: previousData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'daily-input',
                previousFilters?.gte,
                previousFilters?.lte,
                previousFilters?.sales_manager_codes,
            ],
            queryFn: () => getDailyInput(previousFilters!),
            enabled: !!previousFilters?.gte && !!previousFilters?.lte,
        },
        {}
    )

    const granularity = getGranularity(filters.gte, filters.lte)

    // Build chart data: all period slots filled with actual counts
    const chartData = useMemo(() => {
        if (!filters.gte || !filters.lte) return []
        const slots = generatePeriodSlots(filters.gte, filters.lte, granularity)

        data?.forEach((item) => {
            const key = getPeriodKey(dayjs(item.date), granularity)
            if (slots[key])
                slots[key].new_customers_count += item.new_customers_count
        })

        return Object.values(slots).sort((a, b) => a.key.localeCompare(b.key))
    }, [data, granularity, filters.gte, filters.lte])

    // Aggregate by employee for table view
    const employeeData = useMemo(() => {
        if (!data) return []
        const grouped: Record<
            number,
            {
                sales_manager_code: number
                employee_name: string
                new_customers_count: number
            }
        > = {}
        data.forEach((item) => {
            const key = item.sales_manager_code
            if (!grouped[key])
                grouped[key] = {
                    sales_manager_code: key,
                    employee_name: item.employee_name,
                    new_customers_count: 0,
                }
            grouped[key].new_customers_count += item.new_customers_count
        })
        return Object.values(grouped).sort(
            (a, b) => b.new_customers_count - a.new_customers_count
        )
    }, [data])

    // Previous period employee lookup
    const previousEmployeeData = useMemo(() => {
        if (!previousData) return {}
        return previousData.reduce(
            (acc, item) => {
                acc[item.sales_manager_code] =
                    (acc[item.sales_manager_code] || 0) +
                    item.new_customers_count
                return acc
            },
            {} as Record<number, number>
        )
    }, [previousData])

    const totalContacts = useMemo(
        () =>
            chartData.reduce((sum, item) => sum + item.new_customers_count, 0),
        [chartData]
    )

    const previousTotalContacts = useMemo(
        () =>
            previousData?.reduce(
                (sum, item) => sum + item.new_customers_count,
                0
            ) ?? 0,
        [previousData]
    )

    const growthRate = useMemo(() => {
        if (previousTotalContacts === 0) return null
        return (
            ((totalContacts - previousTotalContacts) / previousTotalContacts) *
            100
        )
    }, [totalContacts, previousTotalContacts])

    const avgValue = useMemo(
        () =>
            chartData.length === 0
                ? 0
                : Math.round(totalContacts / chartData.length),
        [chartData, totalContacts]
    )

    return {
        chartData,
        employeeData,
        previousEmployeeData,
        totalContacts,
        previousTotalContacts,
        growthRate,
        avgValue,
        granularity,
        renderState,
    }
}
